import { IconArrowUpRight, IconCheck, IconDeviceFloppy, IconSparkles, IconTrash } from "@tabler/icons-react"
import { type FC, useCallback, useEffect, useState } from "react"
import { Navigate, useLocation, useNavigate, useParams } from "react-router"

import { parseIntNumber } from "@forento/core/utilities/number"
import { type RichContentDocument } from "@forento/rich-content"
import { cn } from "@forento/ui/utilities/tailwind"

import AiGenerationFeedback from "~/components/AiGenerationFeedback"
import Button from "~/components/Button"
import EditableLinkField from "~/components/EditableLinkField"
import ImageUploader from "~/components/ImageUploader"
import InputField, { InputTextArea } from "~/components/InputField"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import RichContentEditor from "~/components/RichContentEditor"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import useFileUpload from "~/hooks/useFileUpload"
import {
	exportEditedRichContent,
	loadRichContent,
	parseRichContent,
	type RichContentValue,
} from "~/utilities/richContent"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import AiBlogPostEditChat from "./AiBlogPostEditChat"

const header = <PageBreadcrumb title="Blog post" path={[{ title: "Blog", link: routes.main.websiteBlog() }]} />

const blogPostSlugMaxLength = 160
const aiPanelCollapsedKey = "blog-post-ai-panel-collapsed"

const BlogPostPage = withAccessRequirement("landingPage", header, () => {
	const postId = parseIntNumber(useParams().postId) ?? null

	if (postId === null) return <Navigate to={routes.main.websiteBlog()} replace />

	return <BlogPostEditor postId={postId} />
})

type SaveAction = "draft" | "publish" | "save"

type BlogPostEditorProps = { postId: number }
const BlogPostEditor: FC<BlogPostEditorProps> = ({ postId }) => {
	const platform = usePlatform()!
	const navigate = useNavigate()
	const alert = useAlert()
	const utils = query.useUtils()
	// Set by the Forento AI wizard when it wrote the post without a cover image to describe.
	const { coverImageIdea, generationId } = (useLocation().state ?? {}) as {
		coverImageIdea?: string | null
		generationId?: number
	}

	const post = query.landing.blog.get.useQuery({ postId })

	const [loadedPostId, setLoadedPostId] = useState<number | null>(null)
	const [title, setTitle] = useState("")
	const [slug, setSlug] = useState("")
	const [metaDescription, setMetaDescription] = useState("")
	const [excerpt, setExcerpt] = useState("")
	const [coverImageUrl, setCoverImageUrl] = useState("")
	const [coverImageAlt, setCoverImageAlt] = useState("")
	const [tagsInput, setTagsInput] = useState("")
	const [content, setContent] = useState<RichContentValue | null>(null)
	const [initialContent, setInitialContent] = useState<RichContentDocument | null>(null)
	const [editorKey, setEditorKey] = useState(0)
	const [saveAction, setSaveAction] = useState<SaveAction | null>(null)
	const [isAiPanelExpanded, setAiPanelExpanded] = useState(() => localStorage.getItem(aiPanelCollapsedKey) !== "true")

	const canUseAi = platform.enabledFeatures.includes("ai-generated-courses")

	const handleAiPanelExpandedChange = useCallback((isExpanded: boolean) => {
		setAiPanelExpanded(isExpanded)
		localStorage.setItem(aiPanelCollapsedKey, (!isExpanded).toString())
	}, [])

	const imageUpload = useFileUpload()
	const updatePost = query.landing.blog.update.useMutation({
		onSuccess: savedPost => {
			utils.landing.blog.get.setData({ postId }, savedPost)
			utils.landing.blog.list.invalidate()
			loadPostState(savedPost)
		},
		onSettled: () => setSaveAction(null),
	})
	const deletePost = query.landing.blog.delete.useMutation({
		onSuccess: async () => {
			await utils.landing.blog.list.invalidate()
			navigate(routes.main.websiteBlog())
		},
	})

	const loadPostState = (value: NonNullable<typeof post.data>) => {
		setLoadedPostId(value.id)
		setTitle(value.title)
		setSlug(value.slug)
		setMetaDescription(value.metaDescription ?? "")
		setExcerpt(value.excerpt ?? "")
		setCoverImageUrl(value.coverImageUrl ?? "")
		setCoverImageAlt(value.coverImageAlt ?? "")
		setTagsInput(value.tags.join(", "))
		setContent(loadRichContent(value.content))
		setInitialContent(parseRichContent(value.content))
		setEditorKey(key => key + 1)
	}

	useEffect(() => {
		if (post.data === undefined || loadedPostId === post.data.id) return
		loadPostState(post.data)
	}, [post.data, loadedPostId])

	// An applied edit rewrites the post that is already loaded, so the effect above would not pick it up.
	const handleAiEditApplied = async () => {
		const updatedPost = await utils.landing.blog.get.fetch({ postId })
		await utils.landing.blog.list.invalidate()
		loadPostState(updatedPost)
	}

	const handleCoverImageUpload = async (file: File) => {
		try {
			const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
			if (uploaded === null) return
			setCoverImageUrl(uploaded.publicUrl)
		} catch (error) {
			await alert.show("Error", error instanceof Error ? error.message : "Failed to upload cover image.")
		}
	}

	const handleSave = async (action: SaveAction) => {
		const trimmedTitle = title.trim()
		const trimmedSlug = slug.trim()
		const tags = tagsInput
			.split(",")
			.map(tag => tag.trim())
			.filter(tag => tag.length > 0)

		if (trimmedTitle.length === 0) {
			await alert.show("Missing title", "Add a title before saving the blog post.")
			return
		}
		if (trimmedSlug.length === 0) {
			await alert.show("Missing slug", "Add a slug before saving the blog post.")
			return
		}

		try {
			setSaveAction(action)
			await updatePost.mutateAsync({
				postId,
				title: trimmedTitle,
				slug: trimmedSlug,
				metaDescription,
				excerpt,
				coverImageUrl,
				coverImageAlt,
				tags,
				isPublished: action === "save" ? undefined : action === "publish",
				content: exportEditedRichContent(content),
			})
		} catch (error) {
			await alert.show("Error", error instanceof Error ? error.message : "Failed to save blog post.")
		}
	}

	const handleDelete = async () => {
		const dialog = await alert.confirm("Delete blog post", "Are you sure you want to delete this blog post?")
		if (!dialog.result) return

		try {
			await deletePost.mutateAsync({ postId })
			dialog.close()
		} catch (error) {
			dialog.close()
			await alert.show("Error", error instanceof Error ? error.message : "Failed to delete blog post.")
		}
	}

	const isPostLoaded = post.data !== undefined
	const isPublished = post.data?.publishDate != null
	const isSaving = updatePost.isPending
	const isUploadingCoverImage = imageUpload.isPending
	const isActionDisabled = !isPostLoaded || loadedPostId !== post.data?.id || isSaving || isUploadingCoverImage
	const postLinkDomain = platform.domain.landingPage ?? platform.domain.platform
	const publicUrl =
		post.data?.publishDate != null && platform.domain.landingPage
			? `//${platform.domain.landingPage}/blog/${post.data.slug}`
			: null
	const hasUnsavedChanges =
		post.data !== undefined &&
		loadedPostId === post.data.id &&
		(title !== post.data.title ||
			slug !== post.data.slug ||
			metaDescription !== (post.data.metaDescription ?? "") ||
			excerpt !== (post.data.excerpt ?? "") ||
			coverImageUrl !== (post.data.coverImageUrl ?? "") ||
			coverImageAlt !== (post.data.coverImageAlt ?? "") ||
			tagsInput !== post.data.tags.join(", ") ||
			(content?.isModified ?? false))

	return (
		<Layout
			asideContent={
				canUseAi && (
					<AiBlogPostEditChat
						isExpanded={isAiPanelExpanded}
						postId={postId}
						hasUnsavedChanges={hasUnsavedChanges}
						onExpandedChange={handleAiPanelExpandedChange}
						onApplied={handleAiEditApplied}
					/>
				)
			}
		>
			<PageHeader>
				<PageBreadcrumb
					title={post.data?.title ?? "Blog post"}
					path={[{ title: "Blog", link: routes.main.websiteBlog() }]}
				/>
				<div className="flex flex-wrap gap-3">
					{canUseAi && (
						<Button
							variant="secondary"
							onClick={() => handleAiPanelExpandedChange(!isAiPanelExpanded)}
							className={cn(isAiPanelExpanded && "bg-primary/10")}
						>
							<IconSparkles className="size-4" />
							{isAiPanelExpanded ? "Hide Forento AI" : "Edit with Forento AI"}
						</Button>
					)}
					{publicUrl !== null && (
						<Button variant="secondary" onClick={publicUrl} newTab>
							<IconArrowUpRight className="size-4" />
							View post
						</Button>
					)}
					<Button
						variant="secondary-danger"
						onClick={handleDelete}
						isLoading={deletePost.isPending}
						isDisabled={isActionDisabled}
					>
						<IconTrash className="size-4" />
						Delete
					</Button>
					{!isPublished && (
						<Button
							variant="secondary"
							onClick={() => handleSave("draft")}
							isLoading={saveAction === "draft" && isSaving}
							isDisabled={isActionDisabled}
						>
							<IconDeviceFloppy className="size-4" />
							Save draft
						</Button>
					)}
					<Button
						variant="primary"
						onClick={() => handleSave(isPublished ? "save" : "publish")}
						isLoading={(saveAction === "save" || saveAction === "publish") && isSaving}
						isDisabled={isActionDisabled}
					>
						{isPublished ? <IconDeviceFloppy className="size-4" /> : <IconCheck className="size-4" />}
						{isPublished ? "Save" : "Publish"}
					</Button>
				</div>
			</PageHeader>

			{post.error ? (
				<p>Failed to load blog post.</p>
			) : post.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<div className="flex flex-col gap-8 pb-32">
					<section className="flex flex-col gap-4">
						<h2 className="m-0 text-2xl font-semibold">SEO</h2>
						<EditableLinkField
							label="Post link"
							prefix={`https://${postLinkDomain}/blog/`}
							value={slug}
							onChange={setSlug}
							placeholder="post-slug"
							maxLength={blogPostSlugMaxLength}
							warningText="Changing the slug will change this post link. The previous link will not lead to the article any more."
						/>
						<InputField label="Title" value={title} onChange={setTitle} maxCharacters={300} />
						<div className="flex flex-col gap-1.5">
							<InputTextArea
								label="Meta description"
								value={metaDescription}
								onChange={setMetaDescription}
								maxCharacters={500}
							/>
							<FieldInfo>
								A short summary that can appear in search results and link previews. Keep it clear and
								inviting.
							</FieldInfo>
						</div>
						<div className="flex flex-col gap-1.5">
							<InputTextArea label="Excerpt" value={excerpt} onChange={setExcerpt} maxCharacters={500} />
							<FieldInfo>
								A preview of the article shown on your blog page before readers open the full post.
							</FieldInfo>
						</div>
					</section>

					<section className="flex flex-col gap-4">
						<h2 className="m-0 text-2xl font-semibold">Publishing</h2>
						<div className="flex flex-wrap items-center gap-3">
							<span className="text-sm font-medium text-gray-900">Status</span>
							<span
								className={
									isPublished
										? "w-fit rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"
										: "w-fit rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600"
								}
							>
								{isPublished ? "Published" : "Draft"}
							</span>
						</div>
						<InputField
							label="Tags"
							value={tagsInput}
							onChange={setTagsInput}
							placeholder="SEO, Tutorials, Announcements"
						/>
					</section>

					<section className="flex flex-col gap-4">
						<h2 className="m-0 text-2xl font-semibold">Cover image</h2>
						{coverImageIdea && coverImageUrl.length === 0 && (
							<div className="border-primary/20 bg-primary/5 flex items-start gap-3 rounded-lg border p-4">
								<IconSparkles className="text-primary size-5 shrink-0" />
								<div className="flex flex-col gap-1">
									<span className="text-sm font-semibold text-gray-900">
										Forento AI suggests a cover image
									</span>
									<span className="text-component-secondary text-sm leading-relaxed">
										{coverImageIdea}
									</span>
								</div>
							</div>
						)}
						<ImageUploader
							filePath={coverImageUrl || null}
							onUpload={handleCoverImageUpload}
							onDelete={() => setCoverImageUrl("")}
							recommendedSize="thumbnail"
						/>
						<div className="flex flex-col gap-1.5">
							<InputField
								label="Image alt text"
								value={coverImageAlt}
								onChange={setCoverImageAlt}
								maxCharacters={300}
							/>
							<FieldInfo>
								A description of the cover image for screen readers and for visitors when the image
								cannot load.
							</FieldInfo>
						</div>
					</section>

					<section className="flex flex-col gap-4">
						<h2 className="m-0 text-2xl font-semibold">Article</h2>
						<RichContentEditor
							key={editorKey}
							placeholder="Write your article here..."
							initialValue={initialContent}
							setValue={setContent}
						/>
						{generationId !== undefined && <AiGenerationFeedback id={generationId} type="blog-post" />}
					</section>
				</div>
			)}
		</Layout>
	)
}

type FieldInfoProps = { children: string }
const FieldInfo: FC<FieldInfoProps> = ({ children }) => (
	<p className="text-component-secondary m-0 pr-24 text-sm leading-relaxed">{children}</p>
)

export default BlogPostPage
